/**
 * LeftShield Portal - Connected Sources Page
 * Shows active integration connection status + webhook health.
 * Route: /collectors | Category: Integrations
 */
import React from 'react';
import {
  Body1,
  Body2,
  Caption1,
  Card,
  CardHeader,
  Badge,
  Spinner,
  Button,
  tokens,
} from '@fluentui/react-components';
import {
  PlugConnectedRegular,
  ArrowSyncRegular,
  CheckmarkCircleRegular,
  ErrorCircleRegular,
  SettingsRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { useNavigate } from 'react-router-dom';
import type { IntegrationStatus } from '../types';

const getStatusBadge = (status: IntegrationStatus) => {
  switch (status) {
    case 'connected':
      return (
        <Badge appearance="filled" color="success" icon={<CheckmarkCircleRegular />}>
          Connected
        </Badge>
      );
    case 'configured':
      return (
        <Badge appearance="tint" color="warning">
          Configured
        </Badge>
      );
    case 'error':
      return (
        <Badge appearance="filled" color="danger" icon={<ErrorCircleRegular />}>
          Error
        </Badge>
      );
    default:
      return (
        <Badge appearance="tint" color="informative">
          Available
        </Badge>
      );
  }
};

export const CollectorsPage: React.FC = () => {
  const ps = usePageStyles();
  const navigate = useNavigate();

  const { data: summary, isLoading } = useQuery({
    queryKey: ['connected-sources'],
    queryFn: () => adminApi.getIntegrationsSummary(),
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <Spinner size="large" label="Loading connected sources..." />
      </div>
    );
  }

  const integrations = summary?.integrations ?? [];

  return (
    <div className={ps.page}>
      <PageHeader title="Connected Sources" subtitle="Active integration connections feeding security and compliance telemetry" />

      {/* Summary stats */}
      <div className={ps.metricGrid}>
        <Card>
          <CardHeader
            image={<PlugConnectedRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteGreenForeground1 }} />}
            header={<Body2>Connected</Body2>}
            description={
              <Body1 style={{ fontSize: '1.5rem', fontWeight: 600, color: tokens.colorPaletteGreenForeground1 }}>
                {summary?.totalConnected ?? 0}
              </Body1>
            }
          />
        </Card>
        <Card>
          <CardHeader
            image={<SettingsRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteYellowForeground1 }} />}
            header={<Body2>Configured</Body2>}
            description={
              <Body1 style={{ fontSize: '1.5rem', fontWeight: 600 }}>
                {summary?.totalConfigured ?? 0}
              </Body1>
            }
          />
        </Card>
        <Card>
          <CardHeader
            image={<ErrorCircleRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteRedForeground1 }} />}
            header={<Body2>Errors</Body2>}
            description={
              <Body1 style={{ fontSize: '1.5rem', fontWeight: 600, color: summary?.totalError ? tokens.colorPaletteRedForeground1 : undefined }}>
                {summary?.totalError ?? 0}
              </Body1>
            }
          />
        </Card>
        <Card>
          <CardHeader
            image={<ArrowSyncRegular style={{ fontSize: '1.5rem', color: tokens.colorNeutralForeground3 }} />}
            header={<Body2>Available</Body2>}
            description={
              <Body1 style={{ fontSize: '1.5rem', fontWeight: 600, color: tokens.colorNeutralForeground3 }}>
                {summary?.totalNotConfigured ?? 0}
              </Body1>
            }
          />
        </Card>
      </div>

      {/* Integration list */}
      <Card>
        <CardHeader
          header={<Body1 style={{ fontWeight: 600 }}>Integration Sources</Body1>}
          description={<Body2>Active data sources feeding security and compliance telemetry</Body2>}
        />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {integrations.length === 0 ? (
            <Body1 className={ps.emptyState}>
              No integrations configured. Go to Integrations to connect your first source.
            </Body1>
          ) : (
            integrations.map((integration) => (
              <div
                key={integration.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: tokens.spacingHorizontalL,
                  padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
                  borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                }}
              >
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
                  <Body1 style={{ fontWeight: 500 }}>{integration.name}</Body1>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                    {integration.description}
                  </Caption1>
                </div>
                <Badge appearance="outline" size="small">
                  {integration.type}
                </Badge>
                {integration.itemCount > 0 && (
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                    {integration.itemCount} item{integration.itemCount !== 1 ? 's' : ''}
                  </Caption1>
                )}
                {integration.lastSyncAt && (
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                    Last sync: {new Date(integration.lastSyncAt).toLocaleString()}
                  </Caption1>
                )}
                {getStatusBadge(integration.status)}
                {integration.status !== 'connected' && integration.status !== 'error' && (
                  <Button appearance="primary" size="small" onClick={() => navigate('/integrations')}>
                    Connect
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
        <div style={{ padding: tokens.spacingVerticalM, display: 'flex', justifyContent: 'flex-end' }}>
          <Button appearance="primary" onClick={() => navigate('/integrations')}>
            Manage Integrations
          </Button>
        </div>
      </Card>
    </div>
  );
};
